import { useAuthSession } from './AuthProvider';

/**
 * The read-only view of the signed-in state, for anything that only needs to
 * react to it -- the shell's routing guard, a nav bar showing an email. The
 * three auth route components need more than this (the actual `signIn`,
 * `signUp`, ... methods), which is why they reach `useAuthSession` instead;
 * this hook is the frozen, minimal contract the shell agent builds against.
 *
 * `status: 'loading'` is the moment before Firebase's `onAuthStateChanged`
 * has answered even once -- deliberately its own state rather than defaulting
 * to `'signed-out'`, so nothing that reads this ever flashes a signed-out UI
 * for a user who is, in fact, signed in and just hasn't heard back yet.
 */
export interface SessionState {
  status: 'loading' | 'signed-out' | 'signed-in';
  // `displayName` is optional, not absent-as-null: most existing constructors
  // of this state (every screen outside sign-up) have no name to report, and
  // it is a display field, never an identity key or a discriminator, so
  // widening it costs those constructors nothing.
  user: {
    email: string | null;
    uid: string;
    emailVerified: boolean;
    displayName?: string | null;
  } | null;
}

export function useSession(): SessionState {
  return useAuthSession().state;
}

/** The one action the shell needs beyond the read-only state above: ending
 *  the session. Kept separate from `SessionState` rather than added to it --
 *  that type is read-only data, not an action, and a test holding a bare
 *  `SessionState` (`'loading'`, `'signed-out'`) has no `Session` to draw one
 *  from. */
export function useSignOut(): () => Promise<void> {
  return useAuthSession().session.signOut;
}
